import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Plus, Search, Filter, FileDown, Clock, FileText, Download, ExternalLink, Trash2, Table, List, ArrowRight, RefreshCw
} from 'lucide-react';
import client from '../api/client';
import toast from 'react-hot-toast';

const PAGE_SIZE = 30;

export default function DiaryPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [diaryType, setDiaryType] = useState(null);
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [view, setView] = useState('table'); // default table for global

  useEffect(() => { loadDiaryType(); }, [slug]);
  useEffect(() => { if (diaryType) loadEntries(); }, [diaryType, page]);

  const loadDiaryType = async () => {
    try {
      const { data } = await client.get('/diary/types');
      const found = (data.diary_types || []).find(dt => dt.slug === slug);
      if (found) setDiaryType(found);
      else toast.error('Diary type not found');
    } catch (err) { console.error(err); }
  };

  const loadEntries = async () => {
    setLoading(true);
    try {
      const params = { diary_type_id: diaryType.id, limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      if (search) params.search = search;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await client.get('/diary/entries', { params });
      setEntries(data.entries || []);
      setTotal(data.total || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams({ diary_type_id: diaryType.id });
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const { data } = await client.get(`/diary/entries/export?${params.toString()}`, { responseType: 'blob' });
      const blob = new Blob([data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${slug}_export_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click(); URL.revokeObjectURL(url);
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try { await client.delete(`/diary/entries/${id}`); toast.success('Deleted'); loadEntries(); }
    catch (err) { toast.error('Failed'); }
  };

  const formatDate = (ts) => new Date(ts).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
  });

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // Get all unique field names from change_data
  const allFieldNames = [];
  for (const entry of entries) {
    for (const change of (entry.change_data || [])) {
      if (!allFieldNames.includes(change.field_name)) allFieldNames.push(change.field_name);
    }
  }

  if (!diaryType) return <div className="text-center py-12 text-gray-400">Loading...</div>;

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{diaryType.icon} {diaryType.name}</h1>
          <p className="text-sm text-gray-500">
            {diaryType.description || 'Auto-tracked field changes across all records'}
            {total > 0 && <span className="ml-2 text-gray-400">• {total} entries</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-gray-200 rounded-lg overflow-hidden">
            <button onClick={() => setView('table')}
              className={`p-2 ${view === 'table' ? 'bg-blue-50 text-blue-600' : 'text-gray-400 hover:bg-gray-50'}`}>
              <Table className="w-4 h-4" />
            </button>
            <button onClick={() => setView('timeline')}
              className={`p-2 ${view === 'timeline' ? 'bg-blue-50 text-blue-600' : 'text-gray-400 hover:bg-gray-50'}`}>
              <List className="w-4 h-4" />
            </button>
          </div>
          <button onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm border rounded-lg transition ${showFilters ? 'bg-blue-50 border-blue-200 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            <Filter className="w-4 h-4" /> Filters
          </button>
          <button onClick={handleExport}
            className="flex items-center gap-1.5 px-3 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-gray-800">
            <FileDown className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
                <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && loadEntries()}
                  placeholder="Search entries, records..." className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => { setSearch(''); setDateFrom(''); setDateTo(''); setPage(0); }}
              className="px-3 py-1.5 text-xs text-gray-500">Clear</button>
            <button onClick={() => { setPage(0); loadEntries(); }}
              className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg">Apply</button>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading...</div>
        ) : entries.length === 0 ? (
          <div className="text-center py-12">
            <Clock className="w-10 h-10 text-gray-200 mx-auto mb-2" />
            <p className="text-gray-400">No entries yet</p>
            <p className="text-gray-300 text-xs mt-1">Changes to tracked fields will appear here automatically</p>
          </div>
        ) : view === 'table' ? (
          /* ===== TABLE VIEW ===== */
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 whitespace-nowrap">Date</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 whitespace-nowrap">Record</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 whitespace-nowrap">By</th>
                  {allFieldNames.map(name => (
                    <th key={name} className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 whitespace-nowrap">{name}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {entries.map(entry => (
                  <tr key={entry.id} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{formatDate(entry.entry_date)}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => navigate(`/records/${entry.record_type_slug}/${entry.record_id}`)}
                        className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                        {entry.reference_number || entry.record_title || 'View'}
                        <ExternalLink className="w-3 h-3" />
                      </button>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {entry.creator_first_name} {entry.creator_last_name?.charAt(0)}.
                    </td>
                    {allFieldNames.map(name => {
                      const change = (entry.change_data || []).find(c => c.field_name === name);
                      return (
                        <td key={name} className="px-4 py-3">
                          {change ? (
                            <div className="flex items-center gap-1.5 text-xs">
                              <span className="text-red-400 line-through">{change.old_value || '–'}</span>
                              <ArrowRight className="w-3 h-3 text-gray-300 flex-shrink-0" />
                              <span className="text-green-600 font-medium">{change.new_value || '–'}</span>
                            </div>
                          ) : (
                            <span className="text-gray-200">–</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          /* ===== TIMELINE VIEW ===== */
          <div className="divide-y divide-gray-100">
            {entries.map(entry => (
              <div key={entry.id} className="px-5 py-4 hover:bg-gray-50 transition">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-xs text-gray-500">{formatDate(entry.entry_date)}</span>
                      <span className="text-xs text-gray-300">•</span>
                      <span className="text-xs text-gray-500">{entry.creator_first_name} {entry.creator_last_name}</span>
                      <span className="text-xs text-gray-300">•</span>
                      <button onClick={() => navigate(`/records/${entry.record_type_slug}/${entry.record_id}`)}
                        className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                        {entry.reference_number || entry.record_title} <ExternalLink className="w-3 h-3" />
                      </button>
                    </div>
                    <div className="space-y-1">
                      {(entry.change_data || []).map((change, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <span className="font-medium text-gray-700 min-w-[120px]">{change.field_name}:</span>
                          <span className="text-red-400 line-through">{change.old_value || '(empty)'}</span>
                          <ArrowRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
                          <span className="text-green-600 font-medium">{change.new_value || '(empty)'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <button onClick={() => handleDelete(entry.id)}
                    className="p-1.5 text-gray-300 hover:text-red-500 transition">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
            <span className="text-xs text-gray-400">{page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}</span>
            <div className="flex gap-1">
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                className="px-3 py-1 text-xs border rounded hover:bg-gray-50 disabled:opacity-40">Prev</button>
              <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}
                className="px-3 py-1 text-xs border rounded hover:bg-gray-50 disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
